import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

const Hero = () => {
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 500], [0, 200]);
  const y2 = useTransform(scrollY, [0, 500], [0, -150]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);

  return (
    <section className="relative h-screen w-full flex items-center justify-center overflow-hidden bg-navy-900 perspective-1000">
      {/* Parallax Background */}
      <motion.div 
        style={{ y: y1 }}
        className="absolute inset-0 z-0 opacity-40 mix-blend-overlay"
      >
        <img 
          src="https://images.unsplash.com/photo-1604871000636-074fa5117945?q=80&w=2000&auto=format&fit=crop" 
          alt="Abstract dark texture" 
          className="w-full h-full object-cover scale-110"
        />
      </motion.div>

      <div className="absolute inset-0 z-0 bg-gradient-to-b from-navy-900/40 via-navy-900/80 to-navy-900"></div>

      <motion.div 
        style={{ y: y2, opacity }}
        className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 text-center mt-20"
      >
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.2 }}
        >
          <span className="text-gold-500 uppercase tracking-[0.3em] text-sm md:text-base mb-6 block font-medium">
            The Vogue of PR
          </span>
        </motion.div>

        <motion.h1 
          className="font-serif text-5xl md:text-7xl lg:text-9xl text-white leading-tight mb-8"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.4 }}
        >
          Where Vision <br className="hidden md:block" />
          <span className="italic text-gray-300 font-light translate-z-20">Meets Voice</span>
        </motion.h1>

        <motion.p 
          className="text-gray-300 text-lg md:text-xl max-w-2xl mx-auto font-light mb-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.8 }}
        >
          Stories that matter. Narratives that move. We shape your brand's presence with editorial precision and purposeful storytelling.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 2.2 }}
        >
          <a 
            href="#contact" 
            className="inline-flex items-center space-x-3 bg-gold-500 text-navy-900 px-10 py-5 rounded-none text-sm uppercase tracking-widest font-semibold hover:bg-white transition-all duration-300 shadow-[0_20px_50px_rgba(201,168,76,0.2)] group"
          >
            <span>Tell Your Story</span>
            <span className="group-hover:translate-x-1 transition-transform duration-300">→</span>
          </a>
        </motion.div>
      </motion.div>

      {/* Floating 3D Elements */}
      <motion.div 
        animate={{ y: [0, -20, 0], rotate: [0, 5, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-1/4 right-[10%] w-32 h-32 border border-gold-500/20 hidden lg:block"
      />
      <motion.div 
        animate={{ y: [0, 20, 0], rotate: [0, -5, 0] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        className="absolute bottom-1/4 left-[10%] w-20 h-20 bg-gold-500/5 hidden lg:block"
      />
    </section>
  );
};

export default Hero;